
import { useState, useEffect } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Template } from "@/lib/types";
import { AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { fetchTemplates } from "@/services/templateService";
import { useAuth } from "@/contexts/AuthContext";

interface ReplyActionValue {
  templateId: string;
  saveToSentItems: boolean;
  subject?: string;
  loopPreventionMinutes?: number;
}

interface ReplyActionProps {
  value: ReplyActionValue;
  onChange: (value: ReplyActionValue) => void;
}

export function ReplyAction({ value, onChange }: ReplyActionProps) {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { tenantId } = useAuth();

  useEffect(() => {
    const loadTemplates = async () => {
      if (!tenantId) return;
      
      setIsLoading(true);
      try {
        const templatesData = await fetchTemplates(tenantId);
        setTemplates(templatesData);
        setError(null);
      } catch (err) {
        console.error("Error fetching templates:", err);
        setError("Failed to load templates");
        setTemplates([]);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadTemplates();
  }, [tenantId]);

  const handleTemplateChange = (templateId: string) => {
    setError(null);
    onChange({ ...value, templateId });
  };

  const handleSaveToSentItemsChange = (saveToSentItems: boolean) => {
    onChange({ ...value, saveToSentItems });
  };

  const handleSubjectChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ ...value, subject: e.target.value });
  };

  const handleLoopPreventionChange = (sliderValues: number[]) => {
    const sliderValue = sliderValues[0];
    const actualMinutes = sliderValueToMinutes(sliderValue);
    onChange({ ...value, loopPreventionMinutes: actualMinutes });
  };

  // Convert slider position (0-100) to actual minutes with granular steps
  const sliderValueToMinutes = (sliderValue: number): number => {
    if (sliderValue <= 50) {
      // First 50% of slider: 3-60 minutes (1 minute steps)
      return Math.round(3 + (sliderValue / 50) * 57);
    } else if (sliderValue <= 75) {
      // Next 25% of slider: 60-240 minutes (15 minute steps)
      const progress = (sliderValue - 50) / 25;
      return 60 + Math.round(progress * 12) * 15;
    } else {
      // Last 25% of slider: 240-1440 minutes (60 minute steps)
      const progress = (sliderValue - 75) / 25;
      return 240 + Math.round(progress * 20) * 60;
    }
  };

  // Convert actual minutes back to slider position
  const minutesToSliderValue = (minutes: number): number => {
    if (minutes <= 60) {
      return ((minutes - 3) / 57) * 50;
    } else if (minutes <= 240) {
      const steps = Math.round((minutes - 60) / 15);
      return 50 + (steps / 12) * 25;
    } else {
      const steps = Math.round((minutes - 240) / 60);
      return 75 + (steps / 20) * 25;
    }
  };

  const formatDuration = (minutes: number) => {
    if (minutes < 60) {
      return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (remainingMinutes === 0) {
      return `${hours} hour${hours !== 1 ? 's' : ''}`;
    }
    return `${hours}h ${remainingMinutes}m`;
  };

  const validateTemplate = () => {
    if (!value.templateId) {
      setError("Template selection is required");
      return false;
    }
    setError(null);
    return true;
  };

  const handleRetry = () => {
    setError(null);
    setTemplates([]);
    setIsLoading(true);
    
    // Retry fetching templates
    if (tenantId) {
      fetchTemplates(tenantId)
        .then(templatesData => {
          setTemplates(templatesData);
        })
        .catch(err => {
          console.error("Error fetching templates:", err);
          setError("Failed to load templates");
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setError("Authentication error. Please log in again.");
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="text-sm font-medium">Message Subject</label>
        <Input
          type="text"
          value={value.subject || ""}
          onChange={handleSubjectChange}
          placeholder="Enter reply subject..."
          className="mt-1"
        />
      </div>
      
      <div>
        <label className="text-sm font-medium">Select Template</label>
        
        {isLoading ? (
          <div className="flex items-center justify-center h-10 bg-muted/20 rounded border border-input mt-1">
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
            <span className="text-sm">Loading templates...</span>
          </div>
        ) : error && templates.length === 0 ? (
          <div className="mt-2">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="flex items-center justify-between">
                <span>{error}</span>
                <Button variant="outline" size="sm" onClick={handleRetry}>
                  Retry
                </Button>
              </AlertDescription>
            </Alert>
          </div>
        ) : (
          <Select
            value={value.templateId}
            onValueChange={handleTemplateChange}
            onOpenChange={() => validateTemplate()}
          >
            <SelectTrigger className={!value.templateId ? "text-muted-foreground" : ""}>
              <SelectValue placeholder="Select a template" />
            </SelectTrigger>
            <SelectContent>
              {templates.map((template) => (
                <SelectItem key={template.id} value={template.id}>
                  {template.name}
                </SelectItem>
              ))}
              {templates.length === 0 && (
                <SelectItem value="none" disabled>
                  No templates available
                </SelectItem>
              )}
            </SelectContent>
          </Select>
        )}
        
        {!isLoading && templates.length === 0 && !error && (
          <p className="text-xs text-amber-500 mt-1">
            No email templates available. Create templates first.
          </p>
        )}
        
        {error && templates.length > 0 && (
          <Alert variant="destructive" className="mt-2 py-2">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
      </div>
      
      <div className="flex items-center space-x-2">
        <Switch
          id="save-to-sent"
          checked={value.saveToSentItems}
          onCheckedChange={handleSaveToSentItemsChange}
        />
        <Label htmlFor="save-to-sent">Save reply message in 'Sent Items'</Label>
      </div>

      <div className="space-y-3">
        <div>
          <Label className="text-sm font-medium">Loop Prevention</Label>
          <p className="text-sm text-muted-foreground">
            Do not send another reply to the same sender for:
          </p>
        </div>
        <div className="space-y-2">
          <Slider
            value={[minutesToSliderValue(value.loopPreventionMinutes || 60)]}
            onValueChange={handleLoopPreventionChange}
            min={0}
            max={100}
            step={0.1}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>3 minutes</span>
            <span className="text-foreground font-medium">
              {formatDuration(value.loopPreventionMinutes || 60)}
            </span>
            <span>24 hours</span>
          </div>
        </div>
      </div>
    </div>
  );
}
